/* RA Pre-Order (PDP + independent sticky product bar)
   - Requires Variant metafield custom.preorder_enabled (boolean).
   - Requires Buy Buttons > Show Add to Cart + Enable Pre-Order.
   - Optional geographic targeting compares the current Shopify shopping country (Liquid localization.country.iso_code).
   - Existing product-form.js remains the sole cart-add implementation.
   - Doesn't intercept the separate CHECK OUT buttons, by design.
*/
(() => {
  'use strict';

  const controllers = new Map();
  const FALLBACK_TITLE = 'Pre-Order Information';
  const FALLBACK_CONFIRM = 'CONFIRM & CONTINUE SHOPPING';
  const FALLBACK_HTML = '<div class="ra-preorder__copy rte"><p>This item is available for pre-order. Please confirm before adding it to your cart.</p></div>';

  function splitTargets(raw) {
    return (raw || '').split(/[\n,;]+/).map((value) => value.trim().toLowerCase()).filter(Boolean);
  }

  class RaPreorderController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.ownerSection;
      this.productId = String(root.dataset.productId);
      this.nativeAtcEnabled = root.dataset.nativeAtc === 'true';
      this.geoEnabled = root.dataset.geoEnabled === 'true';
      this.geoTarget = (root.dataset.geoTarget || 'US').trim().toUpperCase();
      this.shoppingCountry = (root.dataset.shoppingCountry || '').trim().toUpperCase();
      this.mainSection = document.getElementById(`shopify-section-${this.sectionId}`);
      this.modal = root.querySelector('[data-ra-preorder-modal]');
      this.panel = root.querySelector('[data-ra-preorder-panel]');
      this.heading = root.querySelector('[data-ra-preorder-heading]');
      this.body = root.querySelector('[data-ra-preorder-body]');
      this.confirm = root.querySelector('[data-ra-preorder-confirm]');
      this.closeButton = root.querySelector('[data-ra-preorder-close]');
      this.variants = new Map();
      this.contents = Array.from(root.querySelectorAll('template[data-ra-preorder-copy]'));
      this.active = null;
      this.busy = false;
      this.frame = 0;
      this.hideTimer = 0;
      this.busyTimer = 0;
      this.originalBodyOverflow = null;
      this.originalBodyPadding = null;
      this.previousFocus = null;
      this.events = new AbortController();
      this.allowedSubmit = null;

      try {
        const data = JSON.parse(root.querySelector('[data-ra-preorder-variants]')?.textContent || '[]');
        data.forEach((variant) => this.variants.set(String(variant.id), variant));
      } catch (error) {
        console.warn('RA Pre-Order: variant configuration could not be read.', error);
      }

      if (!this.modal || !this.panel || !this.mainSection) return;
      this.addListeners();
      this.observer = new MutationObserver(() => this.scheduleSync());
      const observedArea = this.mainSection.querySelector('.product-info') || this.mainSection;
      this.observer.observe(observedArea, { childList: true, subtree: true });
      this.sync();
    }

    getStickyBar() {
      return Array.from(document.querySelectorAll('[data-sticky-product-bar]'))
        .find((bar) => String(bar.dataset.productId) === this.productId) || null;
    }

    getMainForm() {
      return this.mainSection?.querySelector('.js-product-form-main') || null;
    }

    getVariant(form) {
      const id = form?.querySelector('[name="id"]')?.value;
      return id ? this.variants.get(String(id)) || null : null;
    }

    isShoppingCountryEligible() {
      if (!this.geoEnabled) return true;
      const targetCountries = {
        US: ['US'],
        US_CA: ['US', 'CA'],
        CA: ['CA']
      };
      return (targetCountries[this.geoTarget] || []).includes(this.shoppingCountry);
    }

    isPreorder(variant) {
      return Boolean(this.nativeAtcEnabled && this.isShoppingCountryEligible() &&
        variant && variant.preorder === true);
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.sync();
      });
    }

    sync() {
      this.syncForm(this.getMainForm(), 'main');
      const stickyForm = this.getStickyBar()?.querySelector('.js-product-form');
      this.syncForm(stickyForm, 'sticky');
    }

    syncForm(form, mode) {
      if (!form) return;
      const nativeButton = form.querySelector('[name="add"]');
      const preorderButton = form.querySelector(`[data-ra-preorder-trigger="${mode}"]`);
      if (!nativeButton || !preorderButton) return;
      const variant = this.getVariant(form);
      const shouldPreorder = this.isPreorder(variant);
      if (mode === 'sticky' && preorderButton.textContent.trim() !== (this.root.dataset.preorderButtonLabel || 'Pre-Order')) {
        preorderButton.textContent = this.root.dataset.preorderButtonLabel || 'Pre-Order';
      }

      // The native button stays in the DOM because product-form.js queries [name="add"].
      // Hiding it never removes or disables the Shopify add-to-cart implementation.
      const nativeVisible = mode === 'sticky' ? !shouldPreorder : this.nativeAtcEnabled && !shouldPreorder;
      nativeButton.hidden = !nativeVisible;
      preorderButton.hidden = !shouldPreorder;

      preorderButton.disabled = !variant?.available || nativeButton.disabled ||
        nativeButton.getAttribute('aria-disabled') === 'true' || this.busy;
    }

    resolveCopy(variant) {
      const id = String(variant.id).toLowerCase();
      const sku = String(variant.sku || '').trim().toLowerCase();
      const specific = this.contents.find((content) => {
        const targets = splitTargets(content.dataset.targets);
        return targets.length > 0 && (targets.includes(id) || (sku && targets.includes(sku)));
      });
      const general = this.contents.find((content) => splitTargets(content.dataset.targets).length === 0);
      return specific || general || null;
    }

    putCopy(variant) {
      const content = this.resolveCopy(variant);
      this.heading.textContent = content?.dataset.heading?.trim() || FALLBACK_TITLE;
      this.body.innerHTML = content?.innerHTML.trim() || FALLBACK_HTML;
      this.confirm.textContent = content?.dataset.confirmLabel?.trim() || FALLBACK_CONFIRM;
    }

    lockBody() {
      this.originalBodyOverflow = document.body.style.overflow;
      this.originalBodyPadding = document.body.style.paddingRight;
      const width = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      if (width > 0) document.body.style.paddingRight = `${width}px`;
    }

    unlockBody() {
      if (this.originalBodyOverflow === null) return;
      document.body.style.overflow = this.originalBodyOverflow;
      document.body.style.paddingRight = this.originalBodyPadding;
      this.originalBodyOverflow = null;
      this.originalBodyPadding = null;
    }

    open(trigger) {
      if (this.busy || !this.modal.hidden) return;
      const form = trigger.closest('form');
      const variant = this.getVariant(form);
      if (!form || !variant?.available || !this.isPreorder(variant)) return;
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      // Preserve which form and which exact variant initiated the popup.
      this.active = { form, variantId: String(variant.id), trigger };
      this.previousFocus = document.activeElement;
      this.putCopy(variant);
      window.clearTimeout(this.hideTimer);
      this.modal.hidden = false;
      this.lockBody();
      window.requestAnimationFrame(() => {
        this.modal.classList.add('is-open');
        this.panel.focus({ preventScroll: true });
      });
    }

    close(focusTrigger = true) {
      if (this.modal.hidden) return;
      this.modal.classList.remove('is-open');
      this.unlockBody();
      const opener = focusTrigger ? this.active?.trigger : null;
      this.active = null;
      this.hideTimer = window.setTimeout(() => {
        this.modal.hidden = true;
        if (opener?.isConnected && !opener.hidden) opener.focus({ preventScroll: true });
        else if (focusTrigger && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      }, 180);
    }

    addToCart() {
      if (this.busy || !this.active) return;
      const { form, variantId } = this.active;
      const nativeButton = form.querySelector('[name="add"]');
      const variant = this.getVariant(form);

      // Don't ever add a different SKU if selection changed while popup was open.
      if (!nativeButton || !nativeButton.isConnected || !variant?.available ||
          String(variant.id) !== variantId || !this.isPreorder(variant) ||
          nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true') {
        this.close();
        this.scheduleSync();
        return;
      }
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      this.busy = true;
      this.pendingForm = form;
      this.close(false);
      this.scheduleSync();
      window.clearTimeout(this.busyTimer);
      // Network failure/event-handler exceptions should not permanently freeze the button.
      this.busyTimer = window.setTimeout(() => this.releaseBusy(), 30000);

      // Native submit -> existing product-form.js handles cart drawer / toast / cart page.
      // The one-shot permission avoids reopening the popup in the submit safeguard below.
      this.allowedSubmit = form;
      try {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(nativeButton);
        else nativeButton.click();
      } catch (error) {
        console.error('RA Pre-Order: native form submission failed.', error);
        this.releaseBusy();
      } finally {
        this.allowedSubmit = null;
      }
    }

    releaseBusy() {
      window.clearTimeout(this.busyTimer);
      this.pendingForm = null;
      this.busy = false;
      this.scheduleSync();
      // product-form.js removes loading classes after its success animation.
      window.setTimeout(() => this.scheduleSync(), 1500);
    }

    trapTab(event) {
      if (this.modal.hidden || event.key !== 'Tab') return;
      const nodes = Array.from(this.modal.querySelectorAll('button:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'))
        .filter((node) => !node.hidden && node.getClientRects().length);
      if (!nodes.length) {
        event.preventDefault();
        this.panel.focus({ preventScroll: true });
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (document.activeElement === this.panel) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    addListeners() {
      const signal = this.events.signal;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-ra-preorder-trigger]');
        if (!trigger) return;
        const inMain = trigger.dataset.raPreorderTrigger === 'main' &&
          trigger.dataset.raPreorderOwner === this.sectionId && this.mainSection.contains(trigger);
        const inSticky = trigger.dataset.raPreorderTrigger === 'sticky' &&
          this.getStickyBar()?.contains(trigger);
        if (!inMain && !inSticky) return;
        event.preventDefault();
        this.open(trigger);
      }, { signal });

      // Keyboard/other form submission cannot silently bypass the active SKU popup.
      // Separate direct CHECK OUT buttons normally use their own fetch, not submit.
      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form || this.allowedSubmit === form) return;
        const isMainForm = form === this.getMainForm();
        const isStickyForm = form === this.getStickyBar()?.querySelector('.js-product-form');
        if (!isMainForm && !isStickyForm) return;
        const variant = this.getVariant(form);
        if (!this.isPreorder(variant) || !variant.available) return;
        const trigger = form.querySelector(`[data-ra-preorder-trigger="${isMainForm ? 'main' : 'sticky'}"]`);
        if (!trigger) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(trigger);
      }, { capture: true, signal });

      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.confirm?.addEventListener('click', () => this.addToCart(), { signal });
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (this.modal.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else this.trapTab(event);
      }, { signal });

      const variantChanged = () => {
        this.scheduleSync();
        // Product info fragments can be replaced asynchronously by the theme.
        window.setTimeout(() => this.scheduleSync(), 100);
      };
      document.addEventListener('on:variant:change', variantChanged, { signal });
      document.addEventListener('variant:change', variantChanged, { signal });
      document.addEventListener('product:variant-change', variantChanged, { signal });
      document.addEventListener('change', (event) => {
        if (this.mainSection.contains(event.target) || this.getStickyBar()?.contains(event.target)) variantChanged();
      }, { signal });

      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((name) => {
        document.addEventListener(name, (event) => {
          if (this.pendingForm && event.target === this.pendingForm.closest('product-form')) this.releaseBusy();
        }, { signal });
      });
    }

    destroy() {
      this.events.abort();
      this.observer?.disconnect();
      window.cancelAnimationFrame(this.frame);
      window.clearTimeout(this.busyTimer);
      window.clearTimeout(this.hideTimer);
      this.unlockBody();
    }
  }

  function init() {
    document.querySelectorAll('[data-ra-preorder-root]').forEach((root) => {
      const id = root.dataset.ownerSection;
      const oldController = controllers.get(id);
      if (oldController?.root === root) return;
      if (oldController) oldController.destroy();
      controllers.set(id, new RaPreorderController(root));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    const id = event.detail?.sectionId;
    const controller = controllers.get(id);
    if (controller) {
      controller.destroy();
      controllers.delete(id);
    }
  });
})();
